import { useState } from 'react';

import { CheckCircleIcon } from '@heroicons/react/24/outline';

import {
  SCENARIO_FIELD_MAX_LENGTH,
  SCENARIO_ID_PATTERN,
  useCreateScenario,
} from '@/features/decisions/hooks/useCreateScenario';
import { useUserSession } from '@/hooks/useUserSession';

const INPUT_CLASS =
  'w-full rounded-md border border-gray-300 px-3 py-2 text-[14px] text-[#1A1C2E] placeholder:text-[#B0B2BD] focus:border-[#274483] focus:outline-none';

function validateId(value: string): string | null {
  if (!value) return 'Required.';
  if (value.length > SCENARIO_FIELD_MAX_LENGTH) return `Must be ${SCENARIO_FIELD_MAX_LENGTH} characters or fewer.`;
  if (!SCENARIO_ID_PATTERN.test(value)) return 'Letters, numbers, dot, underscore and hyphen only — no spaces.';
  return null;
}

function validateLabel(value: string): string | null {
  if (!value) return 'Required.';
  if (value.length > SCENARIO_FIELD_MAX_LENGTH) return `Must be ${SCENARIO_FIELD_MAX_LENGTH} characters or fewer.`;
  return null;
}

export function CreateScenarioForm() {
  const { email } = useUserSession();
  const createScenario = useCreateScenario();

  const [scenarioRunId, setScenarioRunId] = useState('');
  const [label, setLabel] = useState('');
  const [showErrors, setShowErrors] = useState(false);

  const idError = validateId(scenarioRunId.trim());
  const labelError = validateLabel(label.trim());
  const created = createScenario.data;

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    setShowErrors(true);
    if (idError || labelError) return;

    createScenario.mutate(
      { scenario_run_id: scenarioRunId.trim(), label: label.trim(), created_by: email },
      {
        onSuccess: () => {
          setScenarioRunId('');
          setLabel('');
          setShowErrors(false);
        },
      },
    );
  };

  return (
    <form onSubmit={handleSubmit} className="overflow-hidden rounded-lg border border-gray-200 bg-white">
      <div className="px-6 pt-5 pb-3">
        <h2 className="text-[16px] font-semibold text-[#1A1C2E]">Step 1 — Create a scenario</h2>
        <p className="mt-1 text-[13px] text-[#5C5F72]">
          A scenario groups the runs you want to compare. It holds no config of its own — each run carries its own, so
          the same scenario can hold runs with different candidate sets.
        </p>
      </div>

      <div className="grid gap-5 border-t border-gray-200 px-6 py-5 md:grid-cols-2">
        <label className="block">
          <span className="mb-1.5 block text-[13px] font-semibold text-[#1A1C2E]">Scenario ID</span>
          <input
            value={scenarioRunId}
            onChange={(e) => setScenarioRunId(e.target.value)}
            placeholder="z100-config-4k-sku-20260803"
            className={INPUT_CLASS}
            aria-invalid={Boolean(showErrors && idError)}
          />
          <span className="mt-1.5 block text-[12px] text-[#9295A5]">
            Every run&apos;s config must carry this exact value, and results are partitioned on it, so it cannot be
            changed later.
          </span>
          {showErrors && idError && <span className="mt-1 block text-[12px] text-[#B4291F]">{idError}</span>}
        </label>

        <label className="block">
          <span className="mb-1.5 block text-[13px] font-semibold text-[#1A1C2E]">Name</span>
          <input
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="August 3rd, Life Sciences"
            className={INPUT_CLASS}
            aria-invalid={Boolean(showErrors && labelError)}
          />
          <span className="mt-1.5 block text-[12px] text-[#9295A5]">
            Shown in the backtest list. Free text — rename-friendly, unlike the ID.
          </span>
          {showErrors && labelError && <span className="mt-1 block text-[12px] text-[#B4291F]">{labelError}</span>}
        </label>
      </div>

      <div className="flex items-center justify-between gap-4 border-t border-gray-200 px-6 py-4">
        <div className="min-h-5 text-[13px]">
          {createScenario.isError && <span className="text-[#B4291F]">{createScenario.error.message}</span>}
          {created && (
            <span className="inline-flex items-center gap-1.5 text-[#1B7F53]">
              <CheckCircleIcon className="size-4 shrink-0" />
              Created {created.label}. Submit a run for it in step 2.
            </span>
          )}
        </div>
        <button
          type="submit"
          disabled={createScenario.isPending}
          className="inline-flex shrink-0 items-center justify-center rounded-md bg-[#1E2F52] px-4 py-2 text-[14px] font-semibold text-white enabled:hover:bg-[#274483] disabled:opacity-50"
        >
          {createScenario.isPending ? 'Creating…' : 'Create scenario'}
        </button>
      </div>
    </form>
  );
}
